import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import mapValues from "lodash/mapValues";
import pickBy from "lodash/pickBy";
import uniq from "lodash/uniq";
import { Style, VectorSource } from "mapbox-gl";

import { FeatureTotals } from "fond/map/featureTotalsSummary";
import { Configuration, Layer } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { filterFeatureCollection, isUuidV4 } from "fond/utils";
import { filterFeaturesByGeometry } from "fond/utils/feature";

/**
 * The features the map is drawing from its geojson sources, keyed by the layer or sublayer
 * configuration they belong to, alongside the configurations drawn from vector tiles instead.
 */
export interface MapboxLayerFeatures {
  featuresByLayerConfigId: Record<string, Feature[]>;
  vectorTileLayerConfigIds: string[];
}

export const EMPTY_MAPBOX_LAYER_FEATURES: MapboxLayerFeatures = { featuresByLayerConfigId: {}, vectorTileLayerConfigIds: [] };

interface ReadMapboxLayerFeaturesOptions {
  versionConfig: Configuration;
  /** Layers that exist in the backend, keyed by configuration ID. */
  realLayers: Record<string, Layer>;
}

/**
 * Read the features the map style draws from its geojson sources.
 *
 * Counting the features of layers that exist in the backend is the backend's job, so layer and
 * sublayer configurations associated with a real layer are left out.
 */
export const readMapboxLayerFeatures = (style: Style, { versionConfig, realLayers }: ReadMapboxLayerFeaturesOptions): MapboxLayerFeatures => {
  const geojsonSources: Record<string, FeatureCollection> = {};
  const vectorTileSources: Record<string, VectorSource> = {};
  Object.entries(style.sources).forEach(([sourceId, source]) => {
    if (source.type === "geojson" && "data" in source && source.data) {
      geojsonSources[sourceId] = source.data as FeatureCollection;
    } else if (source.type === "vector") {
      vectorTileSources[sourceId] = source;
    }
  });

  // A given layer/sublayer configuration can give rise to many style configurations, which are transformed
  // into "mapbox layers". Each "mapbox layer" for a given layer/sublayer configuration has the same filter
  // (by construction), so here we're just picking out one filter for each observed layer/sublayer configuration.
  const seenLayerIds = new Map();
  const vectorTileIds = new Set<string>();
  const skip = new Set();

  style.layers?.forEach((layer) => {
    if (!("source" in layer) || !layer?.source || typeof layer.source !== "string") return;

    if (
      // is this layer using a geojson source?
      geojsonSources[layer.source] &&
      // does this layer have the appropriate metadata linking it to a configuration?
      layer.metadata?.itemId &&
      // only consider a layer/sublayer configuration once
      !skip.has(layer.metadata.itemId)
    ) {
      // Don't process another "mapbox layer" for this layer/sublayer configuration.
      skip.add(layer.metadata.itemId);

      // Our goal is to filter down to those layers that don't actually exist (counting the number of features
      // in layers that exist is the backend's job). Because of the various mutations applied to `versionConfig`
      // this _is not_ a simple task at all. We filter out some common cases here.

      // The "mapbox layer" corresponds to a layer configuration associated with a real layer.
      if (layer.metadata.itemId in realLayers) {
        return;
      }

      // The "mapbox layer" corresponds to a sublayer configuration whose parent configuration is associated
      // with a real layer.
      const mapboxLayerConfig = versionConfig.Data.entities[layer.metadata.itemId];
      if (mapboxLayerConfig?.Type === "SUBLAYER" && mapboxLayerConfig.ParentID && mapboxLayerConfig.ParentID in realLayers) {
        return;
      }
      seenLayerIds.set(layer.metadata.itemId, { source: geojsonSources[layer.source], filter: layer.filter ?? null });
    } else if (vectorTileSources[layer.source] && layer?.metadata?.itemId) {
      vectorTileIds.add(layer.metadata.itemId);
    }
  });

  // Collect features using the representative filter for each layer/sublayer configuration.
  const featuresByLayerConfigId: Record<string, Feature[]> = {};
  seenLayerIds.forEach(({ source, filter }, layerConfigId) => {
    featuresByLayerConfigId[layerConfigId] = filterFeatureCollection(source, filter);
  });

  return { featuresByLayerConfigId, vectorTileLayerConfigIds: Array.from(vectorTileIds) };
};

interface CountLayerConfigFeaturesOptions {
  layerConfigs: Array<LayerConfig | SublayerConfig>;
  versionConfig: Configuration;
  /**
   * When provided, only features intersecting this geometry are counted.
   */
  selectionGeometry?: Polygon | MultiPolygon | null;
}

/**
 * Count the features the map draws for each layer and sublayer configuration.
 */
export const countLayerConfigFeatures = (
  { featuresByLayerConfigId, vectorTileLayerConfigIds }: MapboxLayerFeatures,
  { layerConfigs, versionConfig, selectionGeometry }: CountLayerConfigFeaturesOptions
): FeatureTotals => {
  const layerConfigFeatures: Record<string, Feature[]> = selectionGeometry
    ? mapValues(featuresByLayerConfigId, (features) => filterFeaturesByGeometry(features, selectionGeometry))
    : { ...featuresByLayerConfigId };
  const vectorTileIds = new Set(vectorTileLayerConfigIds);

  // If a layer configuration has no styles, then it won't have a "mapbox layer" counting it.
  // In that case, we count it by aggregating all its sublayer features and then counting them.
  layerConfigs.forEach((layerConfig) => {
    if (
      layerConfig.Type !== "LAYER" ||
      layerConfig.ID in layerConfigFeatures ||
      vectorTileIds.has(layerConfig.ID) ||
      layerConfig.Children.length === 0 ||
      // If any sublayer of this configuration is using vector tiles, then we don't
      // want to count this layer.
      layerConfig.Children.some((sublayerId) => vectorTileIds.has(sublayerId))
    )
      return;

    const features: Feature[] = [];
    layerConfig.Children.forEach((sublayerId) => {
      const sublayerConfig = versionConfig.Data.entities[sublayerId];
      if (!sublayerConfig) return;

      features.push(...(layerConfigFeatures?.[sublayerConfig.ID] ?? []));
    });

    // Sublayers are allowed to have duplicate elements (this happens for cost2serve layers when the buckets
    // collapse), so deduplicate features here (by referential equality).
    layerConfigFeatures[layerConfig.ID] = uniq(features);
  });

  const layerConfigFeatureCounts = mapValues(layerConfigFeatures, (features) => features.length);

  // An edge case here is empty layers. An empty layer may not be present in `realLayers`, which means the above
  // will end up counting the features in that layer. That could be fine, but when this happens to a line layer
  // (which uses lengths instead of feature counts) it looks a little strange. TO avoid this, we filter out
  // any empty feature counts that look like they come from an empty layer. This is only a heuristic, but it's
  // the best we've got without a serious rework in our versionConfig handling.
  const featureCounts = pickBy(layerConfigFeatureCounts, (value, key) => value > 0 || !isUuidV4(key));
  return mapValues(featureCounts, (featureCount) => ({ count: featureCount, length: null, preference: "count" as const, cost: null }));
};
